import { PublicClientApplication } from '@azure/msal-browser';
import { API_URL } from './config';

const API_SCOPE = process.env.REACT_APP_API_SCOPE || '';

interface RequestOptions {
	method?: string;
	body?: unknown;
	headers?: Record<string, string>;
	anonymous?: boolean;
}

let _msalInstance: PublicClientApplication | null = null;
let _tokenPromise: Promise<string | null> | null = null;

export function initializeApiClient(msalInstance: PublicClientApplication) {
	_msalInstance = msalInstance;
}

// FEATURE FLAG: AuthRequired bypass — exposes the signed-in email so API functions can
// include it as a query param fallback while the API allows unauthenticated requests.
export function getCurrentUserEmail(): string | null {
	if (!_msalInstance) return null;
	const accounts = _msalInstance.getAllAccounts();
	return (accounts[0]?.idTokenClaims as { email?: string } | undefined)?.email ?? null;
}

async function getAccessToken(): Promise<string | null> {
	if (!_msalInstance) return null;

	const accounts = _msalInstance.getAllAccounts();
	if (accounts.length === 0) return null;

	if (_tokenPromise) return _tokenPromise;

	_tokenPromise = (async () => {
		try {
			const scopes = API_SCOPE
				? [API_SCOPE]
				: ['openid', 'profile', 'offline_access'];
			const response = await _msalInstance!.acquireTokenSilent({
				scopes,
				account: accounts[0],
			});
			return response.idToken;
		} catch {
			// acquireTokenSilent may fail if no B2C session exists;
			// fall back to the cached ID token from the account
			return accounts[0]?.idToken ?? null;
		} finally {
			_tokenPromise = null;
		}
	})();

	return _tokenPromise;
}

type ResponseType = 'json' | 'blob' | 'text';

function parseApiErrorMessage(body: string): string | null {
	if (!body.trim()) return null;
	try {
		const json = JSON.parse(body) as { errorMessage?: string };
		return json.errorMessage?.trim() || null;
	} catch {
		return null;
	}
}

async function readResponseBody(response: Response, responseType: ResponseType): Promise<string | Blob> {
	if (responseType === 'blob') {
		return response.blob();
	}
	return response.text();
}

async function request<T>(
	endpoint: string,
	options: RequestOptions = {},
	responseType: ResponseType = 'json',
): Promise<T> {
	const { method = 'GET', body, headers = {}, anonymous = false } = options;

	const requestHeaders: Record<string, string> = { ...headers };

	if (!anonymous) {
		const token = await getAccessToken();
		if (token) {
			requestHeaders['Authorization'] = `Bearer ${token}`;
		}
	}

	const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
	if (body && !isFormData && !requestHeaders['Content-Type']) {
		requestHeaders['Content-Type'] = 'application/json';
	}

	const response = await fetch(`${API_URL}${endpoint}`, {
		method,
		headers: requestHeaders,
		body: body ? (isFormData ? (body as FormData) : JSON.stringify(body)) : undefined,
	});

	const responseBody = await readResponseBody(response, responseType);
	const responseText = typeof responseBody === 'string' ? responseBody : null;
	const apiErrorMessage = responseText ? parseApiErrorMessage(responseText) : null;

	if (response.status === 401) {
		_msalInstance?.loginRedirect();
		throw new Error(apiErrorMessage ?? 'Unauthorized');
	}

	if (response.status === 403) {
		throw new Error(apiErrorMessage ?? 'Forbidden');
	}

	if (!response.ok) {
		throw new Error(apiErrorMessage ?? `API error: ${response.status}`);
	}

	if (responseType === 'blob') return responseBody as unknown as T;
	if (responseType === 'text') return (responseText ?? '') as unknown as T;
	// Some endpoints return 204 No Content or an empty 200 body.
	if (response.status === 204 || !responseText?.trim()) {
		return {} as T;
	}
	return JSON.parse(responseText) as T;
}

export const apiClient = {
	get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method'>) =>
		request<T>(endpoint, { ...options, method: 'GET' }),

	post: <T>(
		endpoint: string,
		body?: unknown,
		options?: Omit<RequestOptions, 'method' | 'body'>,
	) => request<T>(endpoint, { ...options, method: 'POST', body }),

	delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method'>) =>
		request<T>(endpoint, { ...options, method: 'DELETE' }),

	blob: (endpoint: string, options?: Omit<RequestOptions, 'method'>) =>
		request<Blob>(endpoint, { ...options, method: 'GET' }, 'blob'),

	postText: (
		endpoint: string,
		body?: unknown,
		options?: Omit<RequestOptions, 'method' | 'body'>,
	) => request<string>(endpoint, { ...options, method: 'POST', body }, 'text'),
};
